import { useState, useEffect } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  Layers,
  FileText,
  Landmark,
  Sparkles,
  Send,
  Users,
  Check,
  X,
  GraduationCap,
  Wand2,
  Calendar,
  AlertTriangle,
  HeartPulse,
  BookOpen,
  Globe,
  Shield,
  Building2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { TEMPLATES_BANCO, type ComunicadoTemplate } from '../../pages/municipal/data/comunicadoTemplates';
import { ComunicadoPreviewCore, DEFAULT_PREVIEW_VISIBILITY, type PreviewVisibility } from './ComunicadoPreviewCore';
import { Modal } from '../ui/Modal';
import { ConfirmModal } from '../ui/ConfirmModal';
import type { ComunicadosConfig, ComunicadoWizardData } from './ComunicadosConfig';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ComunicadoWizardData) => Promise<void>;
  config: ComunicadosConfig;
  initialDraft?: Partial<ComunicadoWizardData> | null;
}

const TIPOS = ['AVISO', 'URGENTE', 'EVENTO', 'REUNIAO', 'LEMBRETE'] as const;

const DESTINATARIOS_OPCOES = [
  { id: 'TODOS', label: 'Toda a Comunidade', desc: 'Toda a comunidade escolar (Direção, Equipe, Professores, Famílias e Alunos)', icon: Globe, color: '#2563eb' },
  { id: 'DIRETORES_EQUIPE', label: 'Direção & Equipe', desc: 'Apenas Diretores, Coordenadores e Secretários das escolas', icon: Shield, color: '#059669' },
  { id: 'PROFESSORES', label: 'Professores', desc: 'Apenas o corpo docente e professores regentes', icon: GraduationCap, color: '#d97706' },
  { id: 'RESPONSAVEIS', label: 'Pais & Responsáveis', desc: 'Apenas pais e responsáveis legais das famílias', icon: Users, color: '#7c3aed' },
  { id: 'SERIE', label: 'Série / Ano', desc: 'Alunos e famílias de anos escolares específicos', icon: BookOpen, color: '#0891b2' },
  { id: 'TURMA', label: 'Turma Específica', desc: 'Alunos, responsáveis e professores de uma turma específica', icon: Users, color: '#e11d48' },
] as const;

const SERIES_OPCOES = [
  { id: 'Educação Infantil', label: 'Educação Infantil' },
  { id: '1º Ano', label: '1º Ano' },
  { id: '2º Ano', label: '2º Ano' },
  { id: '3º Ano', label: '3º Ano' },
  { id: '4º Ano', label: '4º Ano' },
  { id: '5º Ano', label: '5º Ano' },
  { id: '6º Ano', label: '6º Ano' },
  { id: '7º Ano', label: '7º Ano' },
  { id: '8º Ano', label: '8º Ano' },
  { id: '9º Ano', label: '9º Ano' },
  { id: '1ª Série EM', label: '1ª Série EM' },
  { id: '2ª Série EM', label: '2ª Série EM' },
  { id: '3ª Série EM', label: '3ª Série EM' },
  { id: 'EJA', label: 'EJA' },
];

const CATEGORIAS = [
  { id: 'OPERACIONAL', label: 'Operacional', desc: 'Faltas, reuniões, boletins e avisos', icon: FileText, color: '#2563eb' },
  { id: 'INSTITUCIONAL', label: 'Institucional', desc: 'Obras, campanhas e programas', icon: Landmark, color: '#9333ea' },
  { id: 'PEDAGOGICO', label: 'Pedagógico', desc: 'Projetos, feiras e lições', icon: GraduationCap, color: '#059669' },
  { id: 'TEMPLATE', label: 'Usar Template', desc: 'Modelos prontos para uso', icon: Layers, color: '#d97706' },
] as const;

const STEPS = [
  { n: 1, label: 'Categoria' },
  { n: 2, label: 'Conteúdo' },
  { n: 3, label: 'Público' },
  { n: 4, label: 'Revisão' },
];

export function ComunicadoWizardCore({
  open,
  onClose,
  onSubmit,
  config,
  initialDraft,
}: Props) {
  const [step, setStep] = useState(1);
  const [cat, setCat] = useState('OPERACIONAL');
  const [selectedTemplate, setSelectedTemplate] = useState<ComunicadoTemplate | null>(null);
  const [titulo, setTitulo] = useState('');
  const [corpo, setCorpo] = useState('');
  const [tipo, setTipo] = useState('AVISO');
  const [dest, setDest] = useState<string>('TODOS');
  const [escolaIds, setEscolaIds] = useState<string[]>([]);
  const [escolaAlvoId, setEscolaAlvoId] = useState<string>('');
  const [turmaId, setTurmaId] = useState<string>('');
  const [series, setSeries] = useState<string[]>([]);
  const [turmasEscolaAlvo, setTurmasEscolaAlvo] = useState<{ id: string; nome: string }[]>([]);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [fixado, setFixado] = useState(false);
  const [midiaUrl, setMidiaUrl] = useState<string | undefined>(undefined);
  const [midiaTitulo, setMidiaTitulo] = useState<string | undefined>(undefined);
  const [midiaId, setMidiaId] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [vis, setVis] = useState<PreviewVisibility>({ ...DEFAULT_PREVIEW_VISIBILITY });
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const toggleVis = (key: keyof PreviewVisibility) =>
    setVis((prev) => ({ ...prev, [key]: !prev[key] }));

  // Sincroniza estado inicial ao abrir ou ao receber initialDraft (ex: vindo do banco de mídias)
  useEffect(() => {
    if (open) {
      if (initialDraft) {
        setCat(initialDraft.categoria || 'OPERACIONAL');
        setTitulo(initialDraft.titulo || '');
        setCorpo(initialDraft.corpo || '');
        setTipo(initialDraft.tipo || 'AVISO');
        setDest(initialDraft.destinatario || 'TODOS');
        setEscolaIds(initialDraft.escolaIds || []);
        setEscolaAlvoId(initialDraft.escolaAlvoId || '');
        setTurmaId(initialDraft.turmaId || '');
        setSeries(initialDraft.series || initialDraft.configPreview?.series || []);
        setFixado(initialDraft.fixado || false);
        setMidiaUrl(initialDraft.configPreview?.midiaUrl || (initialDraft as any).midiaUrl);
        setMidiaTitulo(initialDraft.configPreview?.midiaTitulo || (initialDraft as any).midiaTitulo);
        setMidiaId(initialDraft.configPreview?.midiaId || (initialDraft as any).midiaId);
      } else {
        // Reset completo para novo formulário
        setStep(1);
        setCat('OPERACIONAL');
        setSelectedTemplate(null);
        setTitulo('');
        setCorpo('');
        setTipo('AVISO');
        setDest('TODOS');
        setEscolaIds([]);
        setEscolaAlvoId('');
        setTurmaId('');
        setSeries([]);
        setFixado(false);
        setMidiaUrl(undefined);
        setMidiaTitulo(undefined);
        setMidiaId(undefined);
        setVis({ ...DEFAULT_PREVIEW_VISIBILITY });
      }
    }
  }, [open, initialDraft]);

  // Carrega turmas quando a escola alvo for selecionada no modo municipal
  useEffect(() => {
    if (escolaAlvoId) {
      setLoadingTurmas(true);
      setTurmaId('');
      api.get(`/escolas/${escolaAlvoId}/turmas`)
        .then((res) => {
          setTurmasEscolaAlvo(res.data || []);
        })
        .catch(() => {
          setTurmasEscolaAlvo([]);
        })
        .finally(() => {
          setLoadingTurmas(false);
        });
    } else {
      setTurmasEscolaAlvo([]);
    }
  }, [escolaAlvoId]);

  // Aplica template selecionado
  const handleSelectTemplate = (tpl: ComunicadoTemplate) => {
    setSelectedTemplate(tpl);
    setTitulo(tpl.titulo);
    setCorpo(tpl.corpo);
    setTipo(tpl.tipo);
    setStep(2);
  };

  // Geração por IA (assistente de redação institucional)
  const handleGenerateAI = async () => {
    if (!titulo.trim()) {
      toast.error('Informe um título ou tema antes de gerar o texto com IA.');
      return;
    }
    setGeneratingAI(true);
    try {
      const prompt = `Redija um comunicado oficial claro, empático e objetivo para a rede escolar com o tema: "${titulo}". Tipo: ${tipo}. Categoria: ${cat}. Inclua saudações e orientações práticas.`;
      const res = await api.post('/ai/generate-text', { prompt, maxTokens: 400 });
      if (res.data?.text) {
        setCorpo(res.data.text);
        toast.success('Texto gerado pela IA com sucesso!');
      } else {
        toast.info('IA indisponível no momento. Preencha o corpo manualmente.');
      }
    } catch {
      toast.info('IA indisponível no momento. Preencha o corpo manualmente.');
    } finally {
      setGeneratingAI(false);
    }
  };

  // Detecta se há dados preenchidos não salvos
  const hasUnsavedChanges = Boolean(
    titulo.trim() || corpo.trim() || selectedTemplate || midiaUrl
  );

  const handleAttemptClose = () => {
    if (hasUnsavedChanges) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    setConfirmDiscardOpen(false);
    onClose();
  };

  const getDestinatarioReviewDesc = () => {
    switch (dest) {
      case 'TODOS':
        return 'Toda a comunidade escolar (Direção, Equipe Gestora, Professores, Famílias e Alunos) das escolas participantes.';
      case 'DIRETORES_EQUIPE':
        return 'Diretores, Vice-Diretores, Coordenadores e Secretários das escolas participantes.';
      case 'PROFESSORES':
        return 'Corpo docente e professores regentes das escolas participantes.';
      case 'RESPONSAVEIS':
        return 'Pais e responsáveis legais das famílias das escolas participantes.';
      case 'SERIE':
        return `Alunos e famílias matriculados nas séries: ${series.length > 0 ? series.join(', ') : 'Todas as séries'}.`;
      case 'TURMA':
        return `Alunos, responsáveis e professores da turma selecionada.`;
      default:
        return 'Destinatários selecionados.';
    }
  };

  // Validação por etapa
  const canAdvance = () => {
    if (step === 1) return true;
    if (step === 2) return titulo.trim().length > 0;
    if (step === 3) {
      if (dest === 'SERIE' && series.length === 0) return false;
      return true;
    }
    return true;
  };

  const handleSubmit = async (status: 'ENVIADO' | 'RASCUNHO') => {
    if (!titulo.trim()) {
      toast.error('O título do comunicado é obrigatório.');
      return;
    }
    setSaving(true);
    try {
      // Backend compatível: se dest for SERIE ou DIRETORES_EQUIPE, mapeia para enum válido e guarda flag rica no configPreview
      const prismaDestinatario =
        dest === 'DIRETORES_EQUIPE' ? 'TODOS' : dest === 'SERIE' ? 'TODOS' : dest;

      // Backend compatível: CategoriaComunicado no Prisma aceita OPERACIONAL | INSTITUCIONAL | TEMPLATE
      const prismaCategoria =
        cat === 'PEDAGOGICO' ? 'OPERACIONAL' : (cat || 'OPERACIONAL');

      await onSubmit({
        titulo: titulo.trim(),
        corpo: corpo.trim() || (midiaTitulo ? `Confira o anexo: ${midiaTitulo}` : titulo.trim()),
        tipo,
        categoria: prismaCategoria,
        destinatario: prismaDestinatario,
        status,
        escolaIds: config.features.multiSchoolSelect && dest !== 'TURMA' ? (escolaIds.length > 0 ? escolaIds : undefined) : (escolaAlvoId ? [escolaAlvoId] : undefined),
        escolaAlvoId: escolaAlvoId || undefined,
        turmaId: dest === 'TURMA' ? turmaId || undefined : undefined,
        series: dest === 'SERIE' && series.length > 0 ? series : undefined,
        fixado,
        templateId: selectedTemplate?.id,
        configPreview: {
          ...vis,
          midiaUrl,
          midiaTitulo,
          midiaId,
          categoriaOriginal: cat,
          destinatarioOriginal: dest,
          destinatarioLabel: getDestinatarioReviewDesc(),
          series: dest === 'SERIE' && series.length > 0 ? series : undefined,
          escolaAlvoId: escolaAlvoId || undefined,
        },
      });
      toast.success(status === 'ENVIADO' ? 'Comunicado disparado com sucesso!' : 'Rascunho salvo.');
      handleClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Falha ao salvar comunicado.';
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      preventClose={hasUnsavedChanges}
      onAttemptClose={handleAttemptClose}
      size="wide"
      title={
        <div className="ccw-header-container">
          <div className="ccw-stepper-wrap">
            <div className="ccw-stepper">
              {STEPS.map((s, idx) => (
                <div key={s.n} className="ccw-step-item">
                  <div
                    className={`ccw-step-num ${step === s.n ? 'active' : step > s.n ? 'done' : ''}`}
                    onClick={() => step > s.n && setStep(s.n)}
                  >
                    {step > s.n ? <Check size={13} /> : s.n}
                  </div>
                  <span className={`ccw-step-lbl ${step === s.n ? 'active' : ''}`}>{s.label}</span>
                  {idx < STEPS.length - 1 && (
                    <div className={`ccw-step-bar ${step > s.n ? 'done' : ''}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      }
      footerLeft={
        <button
          type="button"
          onClick={handleAttemptClose}
          disabled={saving}
          style={{
            padding: '9px 18px',
            borderRadius: 8,
            border: '1.5px solid #cbd5e1',
            background: '#ffffff',
            color: '#475569',
            fontWeight: 700,
            fontSize: '.82rem',
            cursor: 'pointer',
            transition: 'all .15s ease',
          }}
        >
          Cancelar
        </button>
      }
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={saving}
              style={{
                padding: '9px 18px',
                borderRadius: 8,
                border: '1.5px solid #cbd5e1',
                background: '#ffffff',
                color: '#1e293b',
                fontWeight: 700,
                fontSize: '.82rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'all .15s ease',
              }}
            >
              <ChevronLeft size={16} /> Voltar
            </button>
          )}

          {step < 4 ? (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(4, s + 1))}
              disabled={!canAdvance() || saving}
              style={{
                padding: '9px 22px',
                borderRadius: 8,
                border: 'none',
                background: canAdvance() ? '#2563eb' : '#94a3b8',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '.82rem',
                cursor: canAdvance() ? 'pointer' : 'not-allowed',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: canAdvance() ? '0 2px 6px rgba(37, 99, 235, 0.25)' : 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'all .15s ease',
              }}
            >
              Próximo <ChevronRight size={16} />
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => handleSubmit('RASCUNHO')}
                disabled={saving}
                style={{
                  padding: '9px 16px',
                  borderRadius: 8,
                  border: '1.5px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#334155',
                  fontWeight: 700,
                  fontSize: '.82rem',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                Salvar Rascunho
              </button>
              <button
                type="button"
                onClick={() => handleSubmit('ENVIADO')}
                disabled={saving}
                style={{
                  padding: '9px 22px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '.82rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <Send size={15} /> {saving ? 'Disparando...' : 'Enviar Agora'}
              </button>
            </div>
          )}
        </div>
      }
    >
      <div className="ccw-body animate-fadeIn">
        {/* ETAPA 1: CATEGORIA E TEMPLATES */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h4 style={{ margin: 0, fontSize: '.86rem', fontWeight: 800 }}>Selecione o Tipo de Comunicado</h4>

            <div className="ccw-cat-grid">
              {CATEGORIAS.map((c) => {
                const Icon = c.icon;
                const active = cat === c.id;
                return (
                  <div
                    key={c.id}
                    className={`ccw-cat-card ${active ? 'active' : ''}`}
                    onClick={() => {
                      setCat(c.id);
                      if (c.id !== 'TEMPLATE') setSelectedTemplate(null);
                    }}
                  >
                    <Icon size={24} color={c.color} />
                    <strong>{c.label}</strong>
                    <span>{c.desc}</span>
                  </div>
                );
              })}
            </div>

            {/* Galeria de Templates Prontos */}
            {cat === 'TEMPLATE' && (
              <div style={{ marginTop: 12 }}>
                <h5 style={{ margin: '0 0 10px', fontSize: '.82rem', fontWeight: 700 }}>Modelos Oficiais Disponíveis</h5>
                <div className="ccw-templates-list">
                  {TEMPLATES_BANCO.map((tpl) => (
                    <div
                      key={tpl.id}
                      className={`ccw-template-item ${selectedTemplate?.id === tpl.id ? 'selected' : ''}`}
                      onClick={() => handleSelectTemplate(tpl)}
                    >
                      <div className="ccw-template-icon">
                        <Sparkles size={16} color="#d97706" />
                      </div>
                      <div className="ccw-template-info">
                        <strong>{tpl.titulo}</strong>
                        <p>{tpl.corpo}</p>
                      </div>
                      <button type="button" className="btn-secondary" style={{ fontSize: '.72rem', padding: '4px 10px' }}>
                        Usar Modelo
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ETAPA 2: CONTEÚDO E MÍDIA */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0, fontSize: '.86rem', fontWeight: 800 }}>Conteúdo da Mensagem</h4>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleGenerateAI}
                disabled={generatingAI}
                style={{ fontSize: '.76rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <Wand2 size={13} color="#9333ea" />
                {generatingAI ? 'Gerando Texto...' : 'Melhorar com IA'}
              </button>
            </div>

            <div className="mdc-form-field">
              <label>Classificação do Comunicado</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                {TIPOS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTipo(t)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 6,
                      fontSize: '.76rem',
                      fontWeight: 700,
                      border: tipo === t ? '1.5px solid #2563eb' : '1px solid var(--border-color)',
                      background: tipo === t ? '#eff6ff' : 'var(--bg-card)',
                      color: tipo === t ? '#1d4ed8' : 'var(--text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="mdc-form-field">
              <label>Título do Comunicado *</label>
              <input
                type="text"
                className="mdc-form-input"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex: Volta às Aulas 2026 - Orientações Oficiais"
                maxLength={120}
              />
            </div>

            <div className="mdc-form-field">
              <label>Mensagem / Corpo da Comunicação</label>
              <textarea
                className="mdc-form-textarea"
                rows={5}
                value={corpo}
                onChange={(e) => setCorpo(e.target.value)}
                placeholder="Escreva a mensagem clara e objetiva para os destinatários..."
              />
            </div>

            {/* Mídia Anexada (se houver) */}
            {midiaUrl && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img src={midiaUrl} alt="Mídia" style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover' }} />
                  <div>
                    <strong style={{ fontSize: '.8rem', color: '#1e3a8a', display: 'block' }}>
                      {midiaTitulo || 'Mídia Anexa do Banco'}
                    </strong>
                    <span style={{ fontSize: '.72rem', color: '#3b82f6' }}>Anexo incorporado com sucesso</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setMidiaUrl(undefined); setMidiaTitulo(undefined); setMidiaId(undefined); }}
                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '.76rem', fontWeight: 600 }}
                >
                  Remover
                </button>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <input
                type="checkbox"
                id="fixar-comunicado-chk"
                checked={fixado}
                onChange={(e) => setFixado(e.target.checked)}
                style={{
                  width: 17,
                  height: 17,
                  borderRadius: 4,
                  border: '1.5px solid #cbd5e1',
                  background: '#ffffff',
                  accentColor: '#2563eb',
                  cursor: 'pointer',
                }}
              />
              <label htmlFor="fixar-comunicado-chk" style={{ fontSize: '.78rem', cursor: 'pointer', fontWeight: 600, color: '#334155' }}>
                Fixar este comunicado no topo do feed e no Mural Digital
              </label>
            </div>
          </div>
        )}

        {/* ETAPA 3: PÚBLICO & DESTINATÁRIOS (6 MODOS COMPLETOS) */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: '.88rem', fontWeight: 800 }}>Quem deve receber este comunicado?</h4>
              <p style={{ margin: 0, fontSize: '.76rem', color: '#64748b' }}>
                Selecione o grupo de destinatários e refine os filtros de escolas, séries ou turmas.
              </p>
            </div>

            {/* Grid dos 6 Modos de Destinatário */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 10,
            }}>
              {DESTINATARIOS_OPCOES.map((d) => {
                const Icon = d.icon;
                const active = dest === d.id;
                return (
                  <div
                    key={d.id}
                    onClick={() => setDest(d.id)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: 10,
                      border: active ? `2px solid ${d.color}` : '1.5px solid #e2e8f0',
                      background: active ? '#f8fafc' : '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      boxShadow: active ? `0 2px 8px ${d.color}25` : '0 1px 2px rgba(0,0,0,0.02)',
                      transition: 'all .15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: `${d.color}15`, color: d.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon size={16} />
                      </div>
                      {active && <Check size={16} style={{ color: d.color, strokeWidth: 3 }} />}
                    </div>
                    <strong style={{ fontSize: '.84rem', color: '#0f172a' }}>{d.label}</strong>
                    <span style={{ fontSize: '.72rem', color: '#64748b', lineHeight: 1.3 }}>{d.desc}</span>
                  </div>
                );
              })}
            </div>

            {/* SELEÇÃO POR SÉRIE / ANO ESCOLAR */}
            {dest === 'SERIE' && (
              <div style={{
                padding: 14, background: '#f0fdfa', borderRadius: 10,
                border: '1.5px solid #99f6e4', display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ fontSize: '.8rem', fontWeight: 800, color: '#0f766e', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <BookOpen size={15} /> Selecione as Séries / Anos Escolares:
                  </label>
                  <span style={{ fontSize: '.72rem', color: '#0d9488', fontWeight: 600 }}>
                    {series.length === 0 ? 'Nenhuma série marcada (marque ao menos uma)' : `${series.length} série(s) selecionada(s)`}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {SERIES_OPCOES.map((s) => {
                    const checked = series.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                          setSeries((prev) =>
                            checked ? prev.filter((id) => id !== s.id) : [...prev, s.id]
                          );
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 20,
                          border: checked ? '1.5px solid #0d9488' : '1px solid #cbd5e1',
                          background: checked ? '#0d9488' : '#ffffff',
                          color: checked ? '#ffffff' : '#334155',
                          fontSize: '.76rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {checked && '✓ '}
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SELEÇÃO POR TURMA ESPECÍFICA */}
            {dest === 'TURMA' && (
              <div style={{
                padding: 14, background: '#fff1f2', borderRadius: 10,
                border: '1.5px solid #fecdd3', display: 'flex', flexDirection: 'column', gap: 12,
              }}>
                <strong style={{ fontSize: '.82rem', color: '#be123c', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Users size={15} /> Definição da Turma Alvo
                </strong>

                {/* Se for escopo Municipal: Seletor em Cascata Escola -> Turma */}
                {config.scope === 'MUNICIPAL' && config.escolas && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: '.74rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: 4 }}>
                        1º Selecione a Escola *
                      </label>
                      <select
                        value={escolaAlvoId}
                        onChange={(e) => setEscolaAlvoId(e.target.value)}
                        style={{
                          width: '100%', padding: '8px 12px', borderRadius: 8,
                          border: '1.5px solid #cbd5e1', background: '#ffffff', color: '#0f172a',
                          fontSize: '.82rem', outline: 'none',
                        }}
                      >
                        <option value="">Selecione uma escola...</option>
                        {config.escolas.map((esc) => (
                          <option key={esc.id} value={esc.id}>{esc.nome}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: '.74rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: 4 }}>
                        2º Selecione a Turma da Escola
                      </label>
                      <select
                        value={turmaId}
                        onChange={(e) => setTurmaId(e.target.value)}
                        disabled={!escolaAlvoId || loadingTurmas}
                        style={{
                          width: '100%', padding: '8px 12px', borderRadius: 8,
                          border: '1.5px solid #cbd5e1', background: '#ffffff', color: '#0f172a',
                          fontSize: '.82rem', outline: 'none',
                        }}
                      >
                        <option value="">
                          {loadingTurmas ? 'Carregando turmas...' : !escolaAlvoId ? 'Selecione a escola primeiro' : 'Todas as turmas desta escola'}
                        </option>
                        {turmasEscolaAlvo.map((t) => (
                          <option key={t.id} value={t.id}>{t.nome}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Se for escopo Escolar: Seletor Direto de Turmas da Escola */}
                {config.scope === 'ESCOLA' && config.turmas && (
                  <div>
                    <label style={{ fontSize: '.74rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: 4 }}>
                      Selecione a Turma da Escola
                    </label>
                    <select
                      value={turmaId}
                      onChange={(e) => setTurmaId(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 12px', borderRadius: 8,
                        border: '1.5px solid #cbd5e1', background: '#ffffff', color: '#0f172a',
                        fontSize: '.82rem', outline: 'none',
                      }}
                    >
                      <option value="">Todas as Turmas da Escola</option>
                      {config.turmas.map((tm) => (
                        <option key={tm.id} value={tm.id}>{tm.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Seletor Multi-Escola (para escopo municipal nos demais modos) */}
            {config.features.multiSchoolSelect && config.escolas && dest !== 'TURMA' && (
              <div style={{ marginTop: 4 }}>
                <label style={{ fontSize: '.76rem', fontWeight: 700, display: 'block', marginBottom: 8, color: '#334155' }}>
                  Escolas Participantes ({escolaIds.length === 0 ? 'Todas as Escolas da Rede' : `${escolaIds.length} selecionadas`})
                </label>
                <div style={{ maxHeight: 150, overflowY: 'auto', border: '1.5px solid #cbd5e1', borderRadius: 8, padding: 8, background: '#ffffff' }}>
                  {config.escolas.map((esc) => {
                    const checked = escolaIds.includes(esc.id);
                    return (
                      <div
                        key={esc.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', cursor: 'pointer' }}
                        onClick={() => {
                          setEscolaIds((prev) =>
                            checked ? prev.filter((id) => id !== esc.id) : [...prev, esc.id]
                          );
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          readOnly
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: '1.5px solid #cbd5e1',
                            background: '#ffffff',
                            accentColor: '#2563eb',
                            cursor: 'pointer',
                          }}
                        />
                        <span style={{ fontSize: '.78rem', color: '#0f172a' }}>{esc.nome}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ETAPA 4: REVISÃO & WHATSAPP PREVIEW */}
        {step === 4 && (
          <div className="ccw-review-grid">
            <div>
              <h4 style={{ margin: '0 0 10px', fontSize: '.84rem', fontWeight: 800 }}>Pré-visualização do Layout Final</h4>
              <ComunicadoPreviewCore
                titulo={titulo}
                corpo={corpo}
                tipo={tipo}
                categoria={cat}
                destinatario={dest}
                midiaUrl={midiaUrl}
                branding={config.branding}
                visibility={vis}
              />
            </div>

            <div>
              <h4 style={{ margin: '0 0 10px', fontSize: '.84rem', fontWeight: 800 }}>Controles de Exibição Institucional</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--bg-main)', padding: 14, borderRadius: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.76rem', cursor: 'pointer', color: '#334155', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={vis.mostrarLogo}
                    onChange={() => toggleVis('mostrarLogo')}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      border: '1.5px solid #cbd5e1',
                      background: '#ffffff',
                      accentColor: '#2563eb',
                      cursor: 'pointer',
                    }}
                  />
                  Exibir Brasão / Logo Institucional
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.76rem', cursor: 'pointer', color: '#334155', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={vis.mostrarData}
                    onChange={() => toggleVis('mostrarData')}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      border: '1.5px solid #cbd5e1',
                      background: '#ffffff',
                      accentColor: '#2563eb',
                      cursor: 'pointer',
                    }}
                  />
                  Exibir Data da Publicação
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.76rem', cursor: 'pointer', color: '#334155', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={vis.mostrarDestinatario}
                    onChange={() => toggleVis('mostrarDestinatario')}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      border: '1.5px solid #cbd5e1',
                      background: '#ffffff',
                      accentColor: '#2563eb',
                      cursor: 'pointer',
                    }}
                  />
                  Exibir Badge de Destinatário
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.76rem', cursor: 'pointer', color: '#334155', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={vis.mostrarPrefeito}
                    onChange={() => toggleVis('mostrarPrefeito')}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      border: '1.5px solid #cbd5e1',
                      background: '#ffffff',
                      accentColor: '#2563eb',
                      cursor: 'pointer',
                    }}
                  />
                  Exibir Assinatura do Gestor / Direção
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.76rem', cursor: 'pointer', color: '#334155', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={vis.mostrarSlogan}
                    onChange={() => toggleVis('mostrarSlogan')}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      border: '1.5px solid #cbd5e1',
                      background: '#ffffff',
                      accentColor: '#2563eb',
                      cursor: 'pointer',
                    }}
                  />
                  Exibir Slogan da Rede
                </label>
              </div>

              {/* Caixa Informativa com Destinatário Transparente */}
              <div style={{ marginTop: 14, padding: 12, borderRadius: 8, background: '#ecfdf5', border: '1px solid #a7f3d0' }}>
                <strong style={{ fontSize: '.78rem', color: '#065f46', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Check size={14} /> Pronto para Disparo
                </strong>
                <p style={{ margin: '6px 0 0', fontSize: '.74rem', color: '#047857', lineHeight: 1.45 }}>
                  <strong>Público Destinatário:</strong> {getDestinatarioReviewDesc()}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '.72rem', color: '#059669', lineHeight: 1.4 }}>
                  O comunicado ficará disponível nos feeds internos e murais digitais, e disparará alertas push em tempo real nos dispositivos móveis dos usuários autorizados.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmação de descarte de dados não salvos */}
      <ConfirmModal
        open={confirmDiscardOpen}
        title="Descartar alterações?"
        message="Você possui dados preenchidos no formulário. Se fechar agora, as informações não salvas serão perdidas."
        confirmLabel="Sim, descartar"
        cancelLabel="Continuar editando"
        variant="danger"
        onConfirm={handleClose}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </Modal>
  );
}
